import React, { useEffect, useState } from 'react';
import fotoVinhaRotacao1 from '../assets/foto_vinha_rotacao1.jpg';
import fotoVinhaRotacao2 from '../assets/foto_vinha_rotacao2.jpg';
import fotoVinhaRotacao3 from '../assets/foto_vinha_rotacao3.jpg';
import fotoVinhaRotacao4 from '../assets/foto_vinha_rotacao4.jpg';
import { useT } from '../i18n/LanguageContext';

// Imagens do cabeçalho em rotação automática, com fade entre elas —
// mesmo padrão de "várias fotos, uma de cada vez" que já existia (só
// uma imagem estática); aqui alternam sozinhas enquanto a página está
// aberta, sem precisar de nenhuma interação do utilizador. Para trocar
// alguma foto, basta substituir o ficheiro em src/assets com o mesmo
// nome (foto_vinha_rotacao1.jpg .. foto_vinha_rotacao4.jpg) — sem
// espaços no nome do ficheiro, que falham silenciosamente no servidor
// de desenvolvimento (só se nota no build de produção).
const HERO_IMAGES = [fotoVinhaRotacao1, fotoVinhaRotacao2, fotoVinhaRotacao3, fotoVinhaRotacao4];
const HERO_INTERVAL_MS = 5000;

/* 8 castas reais da quinta (Vale da Vilariça) */
const CASTAS = [
  { keyBase: 'vinhas.casta.tn', cor: 'tinta'  }, // Touriga Nacional
  { keyBase: 'vinhas.casta.tf', cor: 'tinta'  }, // Touriga Francesa
  { keyBase: 'vinhas.casta.tr', cor: 'tinta'  }, // Tinta Roriz
  { keyBase: 'vinhas.casta.so', cor: 'tinta'  }, // Sousão
  { keyBase: 'vinhas.casta.ta', cor: 'tinta'  }, // Tinta Amarela
  { keyBase: 'vinhas.casta.rb', cor: 'branca' }, // Rabigato
  { keyBase: 'vinhas.casta.go', cor: 'branca' }, // Gouveio
  { keyBase: 'vinhas.casta.vi', cor: 'branca' }, // Viosinho
];

export default function Vinhas() {
  const t = useT();
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % HERO_IMAGES.length);
    }, HERO_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page-top">

      {/* ── Header ── */}
      <section
        className="section"
        style={{
          position: 'relative',
          overflow: 'hidden',
          paddingTop: 'calc(var(--nav-height) + var(--sp-16))',
        }}
      >
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            aria-hidden={i !== heroIndex}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${src})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              opacity: i === heroIndex ? 1 : 0,
              transition: 'opacity 1.2s ease',
              zIndex: 0,
            }}
          />
        ))}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(160deg, rgba(14,7,4,0.65) 0%, rgba(14,7,4,0.35) 100%)',
            zIndex: 1,
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: '640px' }}>
            <p className="t-label anim-fade-up" style={{ color: '#ffffff', marginBottom: 'var(--sp-4)' }}>
              {t('vinhas.eyebrow')}
            </p>
            <h1 className="t-h1 t-white anim-fade-up delay-1" style={{ marginBottom: 'var(--sp-6)', textTransform: 'uppercase' }}>
              {t('vinhas.title')}
            </h1>
            <p className="t-body-lg anim-fade-up delay-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {t('vinhas.lead')}
            </p>
          </div>
        </div>
      </section>

      {/* ── Mapa Google Maps ── */}
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3018.9851729238765!2d-7.267199923754883!3d41.19458377133917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd41256e4c7c7c7cd%3A0x1234567890abcdef!2sRua%20Vasco%20da%20Gama%2043%2C%205160-297%20Torre%20de%20Monçorvo!5e0!3m2!1spt-PT!2spt!4v1715189765461"
        style={{
          height: '320px',
          width: '100%',
          borderBottom: 'var(--border-thin)',
        }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />

      {/* ── Parcelas ── */}
      <section className="section">
        <div className="container">
          <div className="section-header--centered">
            <p className="t-label" style={{ color: '#000000', marginBottom: 'var(--sp-4)' }}>
              {t('vinhas.parcelasEyebrow')}
            </p>
            <h2 className="t-h2">{t('vinhas.parcelasTitle')}</h2>
          </div>

          <div className="vinhas-castas-grid">
            {CASTAS.map((c) => (
              <article key={c.keyBase} className="casta-card">
                <p
                  className="t-label"
                  style={{
                    color: c.cor === 'tinta' ? 'var(--clr-vinho)' : 'var(--clr-ouro)',
                    marginBottom: 'var(--sp-2)',
                  }}
                >
                  {c.cor === 'tinta' ? 'Casta Tinta' : 'Casta Branca'}
                </p>
                <h3 className="t-h3" style={{ marginBottom: 'var(--sp-4)' }}>
                  {t(`${c.keyBase}.nome`)}
                </h3>
                <p className="t-body t-light" style={{ marginBottom: 'var(--sp-6)' }}>
                  {t(`${c.keyBase}.desc`)}
                </p>
                <dl className="parcela-info-grid">
                  <div>
                    <dt className="t-label" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 'var(--sp-1)' }}>
                      {t('vinhas.field.altitude')}
                    </dt>
                    <dd className="t-small" style={{ fontWeight: 'var(--fw-medium)' }}>
                      {t(`${c.keyBase}.alt`)}
                    </dd>
                  </div>
                  <div>
                    <dt className="t-label" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 'var(--sp-1)' }}>
                      {t('vinhas.field.hectares')}
                    </dt>
                    <dd className="t-small" style={{ fontWeight: 'var(--fw-medium)' }}>
                      {t(`${c.keyBase}.ha`)}
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Filosofia ── */}
      <section
        className="section--sm"
        style={{ backgroundColor: 'var(--clr-areia)', borderTop: 'var(--border-thin)', borderBottom: 'var(--border-thin)' }}
      >
        <div className="container--narrow t-center">
          <blockquote
            className="pullquote"
            style={{
              borderLeft: 'none',
              paddingLeft: 0,
              textAlign: 'center',
              color: 'var(--clr-terra)',
            }}
          >
            {t('vinhas.quote')}
          </blockquote>
          <p
            className="t-small"
            style={{
              marginTop: 'var(--sp-6)',
              letterSpacing: 'var(--ls-wide)',
              textTransform: 'uppercase',
              color: 'var(--clr-cinza)',
            }}
          >
            {t('vinhas.quoteAuthor')}
          </p>
        </div>
      </section>
    </div>
  );
}
